import { HTMLAttributes, useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { Participant, RemoteParticipant } from 'livekit-client'
import { RoomEvent, Track } from 'livekit-client'

import { ChatCloseIcon, Search, VoiceOn, VoiceOff, FocusToggleIcon, CameraIcon, CameraDisabledIcon } from '@/src/assets/icons'

import { useMaybeRoomContext, useMaybeLayoutContext } from '@/src/context'

import { ParticipantsButton } from '@/src/components/controls/ParticipantsButton'
import { ParticipantSearch } from '@/src/components/controls/ParticipantSearch'
import { ParticipantListItem } from '@/src/components/ParticipantListItem'
import { WaitListItem } from '@/src/components/WaitListItem'
import { GroupListItem } from '@/src/components/GroupListItem'
import { CopyUrlButton } from '@/src/components/CopyUrlButton'
import { AutoDistributionModal } from '@/src/components/modals/AutoDistributionModal'

import { useParticipantsList } from '@/src/hooks/participant/useParticipantsList'
import { useGroups } from '@/src/hooks/participant/useGroups'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectIsAdmin, selectSplitRooms } from '@/src/store/slices/meetData'
import { selectRoomState } from '@/src/store/slices/room'
import { selectAccessToken } from '@/src/store/slices/token'

export interface ParticipantListProps extends HTMLAttributes<HTMLDivElement> {
  isPiP?: boolean
  hideCloseBtn?: boolean
}

export function ParticipantList({ isPiP, hideCloseBtn, ...props }: ParticipantListProps) {
  const { t } = useTranslation()
  const roomContext = useMaybeRoomContext()
  const layoutContext = useMaybeLayoutContext()
  const { waitingParticipants, activeParticipants } = useParticipantsList()
  const { groups, getParticipantsByGroup, getCurrentUserGroupId } = useGroups()
  const { text_primary_color, primary_color } = useAppSelector(selectBranding)
  const isAdmin = useAppSelector(selectIsAdmin)
  // Флаг split_rooms из настроек комнаты (админка Platform / Hurma API).
  // Если выключен — организатор не должен видеть автораспределение,
  // завершение групп, список групп и меню перемещения участников.
  // Bitrix не маппит split_rooms, там флаг остаётся false — кнопки скрыты.
  const splitRoomsEnabled = useAppSelector(selectSplitRooms)
  const { livekit_url } = useAppSelector(selectRoomState)
  const accessToken = useAppSelector(selectAccessToken)

  const [filteredActiveParticipants, setFilteredActiveParticipants] = useState<RemoteParticipant[]>(
    [],
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [isMicrophoneDisabled, setIsMicrophoneDisabled] = useState(false)
  const [isLoadingBulkMute, setIsLoadingBulkMute] = useState(false)
  const [isCameraDisabled, setIsCameraDisabled] = useState(false)
  const [isLoadingBulkCam, setIsLoadingBulkCam] = useState(false)
  const [isFocusLocked, setIsFocusLocked] = useState(false)
  const [isLoadingFocusLock, setIsLoadingFocusLock] = useState(false)
  const [isAutoModalOpen, setIsAutoModalOpen] = useState(false)
  const [roomGroups, setRoomGroups] = useState<Array<{ id: string; name: string }>>([])
  // Состояние для аккордеона: main - для основного зала, 'group' для групп, null когда всё свёрнуто
  const [expandedSection, setExpandedSection] = useState<string | null>('main')
  const [isJoiningMainRoom, setIsJoiningMainRoom] = useState(false)
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const localIsCurator =
    roomContext?.localParticipant?.attributes?.isCurator === 'true'

  // Проверяем, может ли пользователь переходить между группами.
  // Дополнительно гейтится флагом split_rooms: если в админке комнаты
  // разделение на группы выключено, кнопки «Войти» / «Вы тут» для перехода
  // между залом и группами не показываем.
  const canJoinGroups = splitRoomsEnabled && (isAdmin || localIsCurator)
  const canManageCurrentRoom = isAdmin || localIsCurator

  // Проверяем, находится ли локальный пользователь в основном зале (не в группе)
  const isInMainRoom = !getCurrentUserGroupId

  // Обработчик переключения секции аккордеона
  const handleAccordionToggle = useCallback((sectionId: string) => {
    setExpandedSection((prev) => (prev === sectionId ? null : sectionId))
  }, [])

  /**
   * Формирует список опций групп для выпадающих списков и меню перемещения участников
   * Включает основную комнату (null) и все существующие группы
   * Если split_rooms отключён - возвращаем пустой массив (скрываем функционал перемещения)
   */
  const groupOptions = useMemo(
    () =>
      splitRoomsEnabled
        ? [
            { id: null, name: t('mainRoom') }, // Основная комната всегда первая опция
            ...roomGroups.map((group) => ({ id: group.id, name: group.name })), // Добавляем все группы
          ]
        : [],
    [roomGroups, splitRoomsEnabled, t],
  )

  const updateToggleStates = useCallback((metadata: string | undefined) => {
    if (!metadata) {
      setIsMicrophoneDisabled(false)
      setIsCameraDisabled(false)
      setIsFocusLocked(false)
      setRoomGroups([])
      setExpandedSection('main') // Возвращаемся к основному залу
      return
    }

    try {
      const parsed = JSON.parse(metadata)
      if (typeof parsed.blockUserMic === 'boolean') {
        setIsMicrophoneDisabled(parsed.blockUserMic)
      } else {
        setIsMicrophoneDisabled(false)
      }
      if (typeof parsed.blockUserCam === 'boolean') {
        setIsCameraDisabled(parsed.blockUserCam)
      } else {
        setIsCameraDisabled(false)
      }
      if (typeof parsed.focusLockEnabled === 'boolean') {
        setIsFocusLocked(parsed.focusLockEnabled)
      } else {
        setIsFocusLocked(false)
      }
      // Обновляем группы из метадаты
      if (Array.isArray(parsed.groups)) {
        const newGroups = parsed.groups
        const hadGroups = roomGroups.length > 0
        const hasNewGroups = newGroups.length > 0

        setRoomGroups(newGroups)

        // Если группы только что созданы (были 0, стало > 0), автоматически раскрываем первую группу
        if (!hadGroups && hasNewGroups) {
          setExpandedSection(newGroups[0].id)
        }
        // Если группы удалены (были > 0, стало 0), возвращаемся к основному залу
        else if (hadGroups && !hasNewGroups) {
          setExpandedSection('main')
        }
      } else {
        setRoomGroups([])
      }
    } catch (e) {
      console.error('Error parsing room metadata:', e)
      setRoomGroups([])
    }
  }, [roomGroups.length])

  useEffect(() => {
    if (!roomContext) return

    const handleMetadataChanged = (metadata: string) => {
      updateToggleStates(metadata)
    }

    if (roomContext.metadata) {
      updateToggleStates(roomContext.metadata)
    }

    roomContext.on(RoomEvent.RoomMetadataChanged, handleMetadataChanged)

    return () => {
      roomContext.off(RoomEvent.RoomMetadataChanged, handleMetadataChanged)
    }
  }, [roomContext, updateToggleStates])

  useEffect(() => {
    const isParticipantsOpen = layoutContext?.widget?.state?.showParticipants

    if (isParticipantsOpen && roomContext?.metadata) {
      updateToggleStates(roomContext.metadata)
    }
  }, [layoutContext?.widget?.state?.showParticipants, roomContext?.metadata, updateToggleStates])

  const handleSearch = useCallback((searchTerm: string) => {
    setSearchTerm(searchTerm)
  }, [])

  // Разделяем участников на группы и обычных (без группы)
  const participantsInGroups = useMemo(() => {
    const inGroups = new Set<string>()
    const participantsByGroup = getParticipantsByGroup

    groups.forEach((group) => {
      const groupParticipants = participantsByGroup.get(group.id) || []
      groupParticipants.forEach((p) => inGroups.add(p.identity))
    })

    return inGroups
  }, [groups, getParticipantsByGroup])

  const participantsWithoutGroup = useMemo(() => {
    const allParticipants = [
      ...(roomContext?.localParticipant && !roomContext.localParticipant.permissions?.hidden
        ? [roomContext.localParticipant]
        : []),
      ...activeParticipants,
    ]

    return allParticipants.filter((participant) => {
      const isTechnicalAgent =
        participant.isAgent === true ||
        participant.permissions?.agent === true ||
        participant.permissions?.recorder === true ||
        participant.identity?.startsWith('agent-')

      if (isTechnicalAgent) return false
      // Исключаем участников, которые в группах
      if (participantsInGroups.has(participant.identity)) return false
      // Применяем поиск
      if (searchTerm) {
        return participant.name.toLowerCase().includes(searchTerm.toLowerCase())
      }
      return true
    })
  }, [activeParticipants, participantsInGroups, roomContext?.localParticipant, searchTerm])

  const pipParticipants = useMemo(() => {
    const allParticipants = [
      ...(roomContext?.localParticipant && !roomContext.localParticipant.permissions?.hidden
        ? [roomContext.localParticipant]
        : []),
      ...activeParticipants,
    ]

    return allParticipants.filter((participant) => {
      const isTechnicalAgent =
        participant.isAgent === true ||
        participant.permissions?.agent === true ||
        participant.permissions?.recorder === true ||
        participant.identity?.startsWith('agent-')

      if (participant.permissions?.hidden || isTechnicalAgent) return false

      if (searchTerm) {
        const participantName = participant.name || participant.identity || ''
        return participantName.toLowerCase().includes(searchTerm.toLowerCase())
      }

      return true
    })
  }, [activeParticipants, roomContext?.localParticipant, searchTerm])

  //Фильтруем участников в группах по поисковому запросу
  const filteredParticipantsByGroup = useMemo(() => {
    const filtered = new Map<string, (Participant | RemoteParticipant)[]>()

    groups.forEach((group) => {
      const groupParticipants = getParticipantsByGroup.get(group.id) || []
      const filteredGroupParticipants = groupParticipants.filter((participant) => {
        // Исключаем скрытых участников, агентов и рекордеров
        if (participant.permissions?.hidden) return false
        if (participant.permissions?.agent || participant.permissions?.recorder) return false
        // Применяем поиск
        if (searchTerm) {
          return participant.name?.toLowerCase().includes(searchTerm.toLowerCase())
        }
        return true
      })
      filtered.set(group.id, filteredGroupParticipants)
    })

    return filtered
  }, [groups, getParticipantsByGroup, searchTerm])

  //Группы, которые должен видеть текущий пользователь
  const visibleRoomGroups = useMemo(() => {
    if (!roomGroups.length) return []

    let groupsToShow = roomGroups

    // Фильтруем по правам доступа
    if (!isAdmin && !localIsCurator && getCurrentUserGroupId) {
      groupsToShow = roomGroups.filter((group) => group.id === getCurrentUserGroupId)
    }

    // При поиске показываем только группы с совпадающими участниками
    if (searchTerm) {
      groupsToShow = groupsToShow.filter((group) => {
        const filteredParticipants = filteredParticipantsByGroup.get(group.id) || []
        return filteredParticipants.length > 0
      })
    }

    return groupsToShow
  }, [roomGroups, isAdmin, localIsCurator, getCurrentUserGroupId, searchTerm, filteredParticipantsByGroup])

  /**
   * Прячем блок "Основной зал" для участников, которые уже находятся в группе,
   * чтобы они не видели пользователей вне своей группы.
   * Админ и куратор всегда видят основной зал.
   */
  const shouldHideMainRoomForLocal =
    roomGroups.length > 0 && !!getCurrentUserGroupId && !isAdmin && !localIsCurator

  useEffect(() => {
    const filteredParticipants = activeParticipants.filter((participant) =>
      participant.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    setFilteredActiveParticipants(filteredParticipants)
  }, [searchTerm, activeParticipants])

  const handleBulkMicToggle = useCallback(async () => {
    if (!livekit_url || !roomContext?.name) return

    setIsLoadingBulkMute(true)

    try {
      const participantIdentities = activeParticipants
        .filter((p) => {
          if (p.metadata) {
            try {
              const metadata = JSON.parse(p.metadata)
              if (metadata.isAdmin === true || metadata.isAdmin === 'true') return false
            } catch (e) {
              // ignore
            }
          }
          if (p.attributes?.isAdmin === 'true' || p.attributes?.isCurator === 'true') {
            return false
          }
          if (p.permissions?.agent || p.permissions?.recorder) {
            return false
          }
          return true
        })
        .map((p) => p.identity)

      const payload = {
        room: roomContext.name,
        participantIdentities,
        grantAccess: isMicrophoneDisabled,
        updateRoomMetadata: true,
      }

      const response = await fetch(
        `${window.origin}/api/block_mute_participants?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
        },
      )

      if (!response.ok) {
        console.error('Failed to bulk mute participants:', await response.text())
      }
    } catch (error) {
      console.error('Error bulk muting participants:', error)
    } finally {
      setIsLoadingBulkMute(false)
    }
  }, [livekit_url, roomContext?.name, isMicrophoneDisabled, activeParticipants, accessToken])

  const handleBulkCamToggle = useCallback(async () => {
    if (!livekit_url || !roomContext?.name) return

    setIsLoadingBulkCam(true)

    try {
      const participantIdentities = activeParticipants
        .filter((p) => {
          if (p.metadata) {
            try {
              const metadata = JSON.parse(p.metadata)
              if (metadata.isAdmin === true || metadata.isAdmin === 'true') return false
            } catch (e) {
              // ignore
            }
          }
          if (p.attributes?.isAdmin === 'true' || p.attributes?.isCurator === 'true') {
            return false
          }
          if (p.permissions?.agent || p.permissions?.recorder) {
            return false
          }
          return true
        })
        .map((p) => p.identity)

      const payload = {
        room: roomContext.name,
        participantIdentities,
        grantAccess: isCameraDisabled,
        updateRoomMetadata: true,
        trackType: 'camera',
      }

      const response = await fetch(
        `${window.origin}/api/block_mute_participants?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
        },
      )

      if (!response.ok) {
        console.error('Failed to bulk disable cameras:', await response.text())
      }
    } catch (error) {
      console.error('Error bulk disabling cameras:', error)
    } finally {
      setIsLoadingBulkCam(false)
    }
  }, [livekit_url, roomContext?.name, isCameraDisabled, activeParticipants, accessToken])

  const handleCreateGroups = useCallback(
    async (groupCount: number, durationSeconds?: number) => {
      if (!splitRoomsEnabled) return
      if (!livekit_url || !roomContext?.name) return

      try {
        const payload = {
          roomName: roomContext.name,
          groupCount,
          ...(durationSeconds !== undefined && { durationSeconds }),
        }

        const response = await fetch(
          `${window.origin}/api/create_groups?livekit_url=${livekit_url}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
              authorization: accessToken || '',
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          const errorText = await response.text()
          console.error('Failed to create groups:', errorText)
        }
      } catch (error) {
        console.error('Error creating groups:', error)
      }
    },
    [livekit_url, roomContext?.name, accessToken, splitRoomsEnabled],
  )

  const handleFinishGroups = useCallback(async () => {
    if (!livekit_url || !roomContext?.name) return

    try {
      const response = await fetch(
        `${window.origin}/api/create_groups?livekit_url=${livekit_url}`,
        {
          method: 'DELETE',
          body: JSON.stringify({ roomName: roomContext.name }),
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
        },
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Failed to finish groups:', errorText)
      }
    } catch (error) {
      console.error('Error finishing groups:', error)
    }
  }, [livekit_url, roomContext?.name, accessToken])

  const handleFocusLockToggle = useCallback(async () => {
    if (!livekit_url || !roomContext?.name) return

    setIsLoadingFocusLock(true)

    try {
      const existingMetadata = JSON.parse(roomContext.metadata || '{}')
      let newMetadata

      if (!isFocusLocked) {
        const focusTrack = layoutContext?.pin.state?.[0]

        newMetadata = {
          ...existingMetadata,
          focusLockEnabled: true,
        }

        if (focusTrack && 'participant' in focusTrack) {
          newMetadata.lockedFocus = {
            participantIdentity: focusTrack.participant.identity,
            trackSource: focusTrack.source,
          }
        } else {
          newMetadata.lockedFocus = null
        }
      } else {
        const { focusLockEnabled, lockedFocus, ...rest } = existingMetadata
        newMetadata = rest
      }

      const bodyReq = {
        roomName: roomContext.name,
        metadata: newMetadata,
      }

      const response = await fetch(
        `${window.origin}/api/update_metadata?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            authorization: accessToken || '',
          },
          body: JSON.stringify(bodyReq),
        },
      )

      if (!response.ok) {
        console.error('Failed to update focus lock:', await response.text())
      }
    } catch (error) {
      console.error('Error updating focus lock:', error)
    } finally {
      setIsLoadingFocusLock(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livekit_url, roomContext?.name, isFocusLocked, layoutContext?.pin.state])

  // Обработчик для перехода в основной зал (из группы)
  const handleJoinMainRoom = useCallback(async () => {
    if (!roomContext?.name || !livekit_url) return

    const identity = roomContext.localParticipant?.identity
    if (!identity) return

    setIsJoiningMainRoom(true)

    // Сбрасываем pin при переходе в основной зал
    layoutContext?.pin.dispatch?.({ msg: 'clear_pin' })

    try {
      const response = await fetch(
        `${window.origin}/api/move_participant?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roomName: roomContext.name,
            participantIdentity: identity,
            groupId: null, // null означает основной зал
          }),
        },
      )

      if (!response.ok) {
        console.error('Failed to join main room:', await response.text())
      }
    } catch (error) {
      console.error('Error joining main room:', error)
    } finally {
      setIsJoiningMainRoom(false)
    }
  }, [roomContext, livekit_url, accessToken, layoutContext?.pin])

  return (
    <div
      {...props}
      className="lk-participant-list"
      style={{
        ...props.style,
        '--lk-accent-bg': primary_color || '#6366f1',
        '--lk-fg': text_primary_color || '#fff',
        '--lk-fg2': text_primary_color || '#fff',
      } as React.CSSProperties}
    >
      <div className="lk-participant-list-container">
        <div className="lk-participant-list-header" style={{ color: text_primary_color }}>
          <span className="lk-participant-list-title">{t('participants')}</span>
          <div className="lk-participant-list-header-actions">
            {(isAdmin || localIsCurator) && (
              <button
                className="lk-header-icon-btn"
                onClick={() => setIsSearchVisible(!isSearchVisible)}
                title={t('search')}
              >
                <Search width={18} height={18} fill={isSearchVisible ? primary_color : text_primary_color} />
              </button>
            )}
            {canManageCurrentRoom && (
              <>
                <button
                  className="lk-header-icon-btn"
                  onClick={handleBulkMicToggle}
                  disabled={isLoadingBulkMute}
                  title={t('bulkMicControl')}
                >
                  {isMicrophoneDisabled ? (
                    <VoiceOff width={18} height={18} fill="#ef4444" />
                  ) : (
                    <VoiceOn width={18} height={18} fill={text_primary_color} />
                  )}
                </button>
                <button
                  className="lk-header-icon-btn"
                  onClick={handleFocusLockToggle}
                  disabled={isLoadingFocusLock}
                  title={t('bulkFocusLockControl')}
                  style={{ color: isFocusLocked ? '#ef4444' : text_primary_color }}
                >
                  <FocusToggleIcon width={18} height={18} />
                </button>
              </>
            )}
            {isAdmin && (
              <button
                className="lk-header-icon-btn"
                onClick={handleBulkCamToggle}
                disabled={isLoadingBulkCam}
                title={t('bulkCamControl')}
              >
                {isCameraDisabled ? (
                  <CameraDisabledIcon width={18} height={18} fill="#ef4444" />
                ) : (
                  <CameraIcon width={18} height={18} fill={text_primary_color} />
                )}
              </button>
            )}
            {!hideCloseBtn && (
              <ParticipantsButton className="lk-close-participant-list" aria-pressed={false} closeBar>
                <ChatCloseIcon />
              </ParticipantsButton>
            )}
          </div>
        </div>

        {isSearchVisible && <ParticipantSearch onSearch={handleSearch} />}

        <div className="lk-participant-list-scrollable">
          {!!waitingParticipants.length && (
            <div className="lk-participant-items">
              <div
                className="lk-participant-list-subheader"
                style={{ color: `${text_primary_color}` }}
              >
                {t('waitingRoom')} {`(${waitingParticipants.length})`}
              </div>
              <div className="lk-participant-list-items-scrollable">
                {waitingParticipants.map((participant) => (
                  <WaitListItem participant={participant} key={participant.identity} />
                ))}
              </div>
            </div>
          )}

          {isPiP && (
            <div className="lk-participant-items">
              <div className="lk-participant-list-items-scrollable">
                {pipParticipants.map((participant) => (
                  <ParticipantListItem
                    key={participant.identity}
                    participant={participant}
                  />
                ))}

                {pipParticipants.length === 0 && (
                  <div className="lk-group-item-empty">{t('emptyGroupMessage')}</div>
                )}
              </div>
            </div>
          )}

          {/* Основной зал (участники без группы).
              Скрываем для обычных участников, которые уже находятся в группе. */}
          {!isPiP && !shouldHideMainRoomForLocal && (
            <div className="lk-group-item">
              <div
                className="lk-group-item-header"
                onClick={() => handleAccordionToggle('main')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleAccordionToggle('main')
                  }
                }}
                aria-expanded={expandedSection === 'main'}
                style={{ color: text_primary_color }}
              >
                <div className="lk-group-item-header-content">
                  <span className="lk-group-item-title">{t('mainRoom')}</span>
                  <span className="lk-group-item-count">{participantsWithoutGroup.length}</span>
                </div>
                <div
                  className="lk-group-item-actions"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginRight: '-4px',
                  }}
                >
                  {/* Кнопка "Войти" / "Вы тут" для основного зала */}
                  {canJoinGroups && roomGroups.length > 0 && (
                    isInMainRoom ? (
                      <span
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: primary_color || '#6366f1',
                          background: primary_color
                            ? `color-mix(in srgb, ${primary_color} 15%, transparent)`
                            : 'rgba(99, 102, 241, 0.15)',
                          borderRadius: '6px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('youAreHere')}
                      </span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleJoinMainRoom()
                        }}
                        disabled={isJoiningMainRoom}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#fff',
                          background: primary_color || '#6366f1',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isJoiningMainRoom ? 'wait' : 'pointer',
                          opacity: isJoiningMainRoom ? 0.7 : 1,
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => {
                          if (!isJoiningMainRoom) {
                            e.currentTarget.style.opacity = '0.85'
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = isJoiningMainRoom ? '0.7' : '1'
                        }}
                      >
                        {isJoiningMainRoom ? t('joining') : t('joinMainRoom')}
                      </button>
                    )
                  )}
                  <svg
                    className={`lk-group-item-chevron ${expandedSection === 'main' ? 'lk-group-item-chevron-expanded' : ''}`}
                    style={{ color: text_primary_color }}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>

              {expandedSection === 'main' && (
                <div className="lk-group-item-participants">
                  {participantsWithoutGroup.map((participant) => (
                    <ParticipantListItem
                      key={participant.identity}
                      participant={participant}
                      groupOptions={groupOptions}
                    />
                  ))}

                  {participantsWithoutGroup.length === 0 && (
                    <div className="lk-group-item-empty">{t('emptyGroupMessage')}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Отображение групп */}
          {!isPiP && splitRoomsEnabled && visibleRoomGroups.length > 0 && (
            <div className="lk-participant-items">
              <div className="lk-participant-list-items-scrollable">
                {visibleRoomGroups.map((group) => {
                  const groupParticipants = filteredParticipantsByGroup.get(group.id) || []
                  const isCurrentUserGroup = getCurrentUserGroupId === group.id
                  return (
                    <GroupListItem
                      key={group.id}
                      groupId={group.id}
                      groupName={group.name}
                      participants={groupParticipants}
                      groupOptions={groupOptions}
                      isCurrentUserGroup={isCurrentUserGroup}
                      isExpanded={expandedSection === group.id}
                      onToggle={() => handleAccordionToggle(group.id)}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Футер с кнопками - прижат к низу */}
        {!isPiP && (
          <div className="lk-participant-list-footer">
            {/* Кнопки управления группами */}
            {isAdmin && splitRoomsEnabled && (
              <div className="lk-participant-list-footer-buttons">
                <button
                  className="lk-button"
                  onClick={() => setIsAutoModalOpen(true)}
                >
                  {t('autoDistribution')}
                </button>
                {roomGroups.length > 0 && (
                  <button
                    className="lk-button lk-button-danger"
                    onClick={handleFinishGroups}
                  >
                    {t('finishGroups')}
                  </button>
                )}
              </div>
            )}
            <CopyUrlButton />
            {splitRoomsEnabled && (
              <AutoDistributionModal
                isOpen={isAutoModalOpen}
                onCancel={() => setIsAutoModalOpen(false)}
                onSubmit={async ({ groupCount, durationSeconds }) => {
                  await handleCreateGroups(groupCount, durationSeconds)
                  setIsAutoModalOpen(false)
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
